/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 预览内容面板组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-12 09:28:51
 */

import React, { useState, useEffect } from 'react'
import { Button, Input, message, Switch, Tag } from 'antd'
import { TestDataItem } from '../hooks/useCSVUpload'
import { useAppSelector } from '../../../../../app/hooks'
import './PreviewContentPanel.scss'
import './TableColumns.scss'

const { TextArea } = Input

interface PreviewContentPanelProps {
  record: TestDataItem
  parseDiffData: (
    diffStr: string
  ) => {
    newItems: Array<{
      item_id: number
      title: string
      content: string
    }>
    modifiedItems: Array<{
      item_id: number
      title: string
      content: string
      originalContent: string
    }>
    deletedItems: Array<{
      item_id: number
      title: string
      content: string
    }>
  }
  onSaveRemark: (
    key: string,
    remark: string,
    isProcessed: boolean,
    processedBy?: string
  ) => void
}

const PreviewContentPanel: React.FC<PreviewContentPanelProps> = ({
  record,
  parseDiffData,
  onSaveRemark
}) => {
  const [remarkInput, setRemarkInput] = useState<string>('')
  const [isProcessed, setIsProcessed] = useState<boolean>(false)

  // 获取当前用户昵称
  const userNickname = useAppSelector(
    (state) => state.main.user?.nickname || ''
  )

  // 同步备注输入框和已处理状态的值
  // 当切换照片（record.key变化）时，重置为当前record的值
  useEffect(() => {
    setRemarkInput(record.remark || '')
    setIsProcessed(record.isProcessed || false)
  }, [record.key, record.remark, record.isProcessed])

  const handleSaveRemark = () => {
    const processedBy = isProcessed ? userNickname : undefined
    onSaveRemark(record.key, remarkInput, isProcessed, processedBy)
    message.success('备注和状态已保存')
  }

  const handleProcessedChange = (checked: boolean) => {
    // 只更新本地状态，不自动保存
    // 用户需要点击"保存"按钮才会保存
    setIsProcessed(checked)
  }

  const { newItems, modifiedItems, deletedItems } = parseDiffData(
    record.diff || ''
  )

  return (
    <div className="preview-content-panel">
      {/* 左侧修改内容展示区域 */}
      <div className="diffSection">
        <div className="title">修改内容</div>
        {newItems.length === 0 &&
        modifiedItems.length === 0 &&
        deletedItems.length === 0 ? (
          <div className="empty">暂无修改内容</div>
        ) : (
          <div className="diff-content-display">
            {/* 新增项 */}
            {newItems.length > 0 &&
              newItems.map((item) => (
                <div key={item.item_id} className="item itemAdd">
                  <span className="itemLabel">新增</span>
                  <span className="itemContent">
                    {item.title}：{item.content}
                  </span>
                </div>
              ))}

            {/* 修改项 */}
            {modifiedItems.length > 0 &&
              modifiedItems.map((item) => (
                <div key={item.item_id} className="item itemModify">
                  <span className="itemLabel">修改</span>
                  <span className="itemContent">
                    {`${item.title}："${item.originalContent}"`}修改为"
                    {`${item.content}"`}
                  </span>
                </div>
              ))}

            {/* 删除项 */}
            {deletedItems.length > 0 &&
              deletedItems.map((item) => (
                <div key={item.item_id} className="item itemDelete">
                  <span className="itemLabel">删除</span>
                  <span className="itemContent">
                    {item.title}: {item.content}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* 右侧备注输入区域 */}
      <div className="remarkSection">
        <div
          className="remarkLabel"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span>备注</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isProcessed && record.processedBy && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                处理人: {record.processedBy}
              </span>
            )}
            <Switch
              checked={isProcessed}
              onChange={handleProcessedChange}
              checkedChildren="已处理"
              unCheckedChildren="未处理"
              size="small"
            />
          </div>
        </div>
        <div className="remarkInputWrapper">
          <TextArea
            value={remarkInput}
            placeholder="请输入备注"
            onChange={(e) => {
              setRemarkInput(e.target.value)
            }}
            className="remarkTextarea"
          />
          <Button
            type="primary"
            size="small"
            onClick={handleSaveRemark}
            className="saveButton"
          >
            保存
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PreviewContentPanel
